import {
	Box,
	Button,
	Divider,
	Flex,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
	useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import OptionItem from "@components/ChakraUI/OptionItem";
import { ConsultantSelectTypeOptions } from "@constants/createOrder";
import type { Consultant } from "@models/Consultant";
import type { BookingType, MenuData, StepOneData, StepThreeData, StepTwoData } from "@models/Order";
import { getAddress } from "@utils/customerUtils";
import LoadingIndicator, { RotatingLoader } from "@components/Loading";
import RightArrow from "@components/icons/RightArrow";
import ConsultantSelectMenu from "./ConsultantSelectMenu";
import FinalizedConsultantItem from "./FinalizedConsultantItem";
import ShiftSummaryItem from "./ShiftSummaryItem";
import { useNavigate } from "react-router-dom";
import BigWhiteCheckIcon from "@components/icons/BigWhiteCheckIcon";
import moment from "moment";
import { useLoadMatchingConsultants, useCustomerTerms, useCustomerCompetences } from "@api/hooks";

interface Props {
	stepOneData: StepOneData;
	stepTwoData: StepTwoData;
	data: StepThreeData;
	onChangeData: (data: StepThreeData) => void;
	toggleNextButtonDisable: (status: boolean) => void;
	customer?: any;
	loadingCustomerData?: boolean;
	jobCreationLoading?: boolean;
	hasErrorInJobCreation?: boolean;
}

const StepThree = ({
	stepOneData,
	stepTwoData,
	customer,
	onChangeData,
	loadingCustomerData = false,
	jobCreationLoading = false,
	hasErrorInJobCreation = false,
}: Props) => {
	const navigate = useNavigate();

	// API hooks
	const { mutate: getMatchingConsultantsMutation, isLoading: loadingMatches, data: matchingConsultants } = useLoadMatchingConsultants();
	const { data: termsData, isLoading: loadingCustomerTerms } = useCustomerTerms(customer?.id);
	const { competences: branches, isLoading: loadingCustomerCompetences } = useCustomerCompetences(customer?.id);

	const terms = termsData?.terms || "";

	const [consultantSelectType, setConsultantSelectType] = useState<BookingType>(
		ConsultantSelectTypeOptions[0].keyValue,
	);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [selectedConsultants, setSelectedConsultants] = useState<Consultant[]>([]);
	const [showTermsModal, setShowTermsModal] = useState<boolean>(false);

	const {
		isOpen: isOpenOrderCreationLoadingModal,
		onOpen: onOpenOrderCreationLoadingModal,
		onClose: onCloseOrderCreationLoadingModal,
	} = useDisclosure();

	useEffect(() => {
		jobCreationLoading && onOpenOrderCreationLoadingModal();
	}, [jobCreationLoading, onOpenOrderCreationLoadingModal]);

	useEffect(() => {
		hasErrorInJobCreation && onCloseOrderCreationLoadingModal();
	}, [hasErrorInJobCreation, onCloseOrderCreationLoadingModal]);

	const loadMatchingConsultants = async () => {
		if (customer) {
			const body: any = {
				competence: {
					branch: stepOneData.competence.branch || "",
					service: stepOneData.competence.service || "",
					skills: stepOneData.competence.skills || [],
				},
				customer: { id: customer.id, name: customer?.name || "" },
				shifts: stepTwoData.shifts.map((shift) => ({
					start: moment(shift.start).toISOString(),
					end: moment(shift.end).toISOString(),
					break: shift.break ?? 0,
					flexible: shift.flexible ?? false,
				})),
			};

			if (stepOneData.competence?.skills?.length === 0) {
				delete body.competence.skills;
			}

			await getMatchingConsultantsMutation(body);
		}
	};

	const [shouldSystemChooseDefaultConsultant, setShouldSystemChooseDefaultConsultant] = useState<boolean>();

	const handleConsultantChooseType = (key: BookingType) => {
		if (key === ConsultantSelectTypeOptions[0].keyValue) {
			setSelectedConsultants([]);
		}
		setConsultantSelectType(key);
		if (key === ConsultantSelectTypeOptions[1].keyValue) {
			onOpen();
			loadMatchingConsultants();
		}
	};

	const handleDrawerData = (data: MenuData) => {
		setSelectedConsultants(data.selectedConsultants);
		setShouldSystemChooseDefaultConsultant(!!data.shouldSystemChooseIfSelectedConsultantsNotAvailable);
		onClose();
	};

	const handleConfirmData = () => {
		onOpenOrderCreationLoadingModal();
		const data = {
			bookingType: consultantSelectType,
			customer: { id: customer?.id as string },
			invitations: selectedConsultants ? selectedConsultants.map((c) => c.id) : undefined,
			allowLaterBooking: shouldSystemChooseDefaultConsultant,
			numPositions: 1,
			internalNotes:
				consultantSelectType === ConsultantSelectTypeOptions[1].keyValue
					? shouldSystemChooseDefaultConsultant
						? "Kund önskar annan konsult om önskad ej kan."
						: "Kunden vill inte boka en annan konsult via systemet."
					: undefined,
		};
		onChangeData(data);
	};

	const isSaveButtonDisabled =
		!consultantSelectType ||
		!customer?.id ||
		(consultantSelectType === ("INVITATION" as BookingType) && selectedConsultants.length === 0);

	const getContactPersonData = () => {
		if (stepOneData.contactPersons && stepOneData.contactPersons.length > 0 && stepOneData.contactPersons[0]) {
			const selectedPerson = stepOneData.contactPersons[0];
			if (selectedPerson.firstName && selectedPerson.lastName)
				return `${selectedPerson.firstName} ${selectedPerson.lastName}`;
		}
		return "";
	};

	const getCompetencesAndSkills = () => {
		const selectedBranch = branches?.find((item) => item.key === stepOneData.competence.branch);
		if (!!selectedBranch) {
			const selectedService = selectedBranch.items?.find((item) => item.key === stepOneData.competence.service);
			if (!!selectedService) {
				const skills =
					stepOneData.competence.skills && !!selectedService
						? selectedService.items?.filter((item) => stepOneData.competence.skills?.includes(item.key))
						: [];
				if (skills && skills.length > 0) {
					return `${selectedService.name.sv}, ${skills.map((item) => item.name.sv).join(" , ")}`;
				}
				return `${selectedService.name.sv}`;
			}
		}
		return "";
	};

	return (
		<>
			<VStack alignItems="start" mb={8} spacing="7">
				<VStack alignItems="start" spacing="3" w="full">
					<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
						Bekräfta uppdrag
					</Text>
					<Divider />
				</VStack>
				<HStack width={"90%"} justify={"space-between"} my={2}>
					<VStack align="stretch" width={"25%"}>
						<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
							Kund
						</Text>
						{loadingCustomerData ? (
							<LoadingIndicator size={"s"} />
						) : (
							<Text fontSize={"16px"} fontWeight={400} my={0}>
								{customer?.name}
							</Text>
						)}
					</VStack>
					<VStack align="stretch" width={"35%"}>
						<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
							Adress, arbetsplats
						</Text>
						<Text fontSize={"16px"} fontWeight={400} my={0}>
							{stepOneData.address ? getAddress(stepOneData.address) : ""}
						</Text>
					</VStack>
					<VStack align="stretch" width={"40%"}>
						<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
							Tjänst
						</Text>
						{loadingCustomerCompetences ? (
							<LoadingIndicator size={"s"} />
						) : (
							<Text fontSize={"16px"} fontWeight={400} my={0}>
								{getCompetencesAndSkills()}
							</Text>
						)}
					</VStack>
				</HStack>
				<VStack alignItems={"flex-start"} w="full">
					<Text fontSize={"14px"} fontWeight={500} color={"#999999"}>
						Arbetspass
					</Text>
					{stepTwoData.shifts.map((summaryItem) => (
						<ShiftSummaryItem key={summaryItem.start + summaryItem.end} {...summaryItem} />
					))}
				</VStack>
				<VStack align="stretch" my={2}>
					<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
						Arbetsbeskrivning
					</Text>
					<Text fontSize={"16px"} fontWeight={400} my={0}>
						{stepOneData.description || "-"}
					</Text>
				</VStack>
				<VStack align="stretch" my={2}>
					<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
						Om arbetsplatsen
					</Text>
					<Text fontSize={"16px"} fontWeight={400} my={0}>
						{stepOneData.customerDescription || "-"}
					</Text>
				</VStack>
				<VStack align="stretch" my={2}>
					<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
						Kontaktperson
					</Text>
					<Text fontSize={"16px"} fontWeight={400} my={0}>
						{getContactPersonData()}
					</Text>
				</VStack>
				<VStack align="stretch" my={2} spacing="5">
					<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={0}>
						Tillsättning av uppdrag
					</Text>
					{ConsultantSelectTypeOptions.map((option) => (
						<OptionItem
							key={option.keyValue}
							keyValue={option.keyValue}
							onChange={(key) => handleConsultantChooseType(key as BookingType)}
							label={option.label}
							isSelected={consultantSelectType === option.keyValue}
							description={option.description}
						/>
					))}
					{shouldSystemChooseDefaultConsultant && (
						<HStack pl={8} alignItems={"flex-start"} spacing="3">
							<span style={{ width: "20px" }}>
								<RightArrow fillColor={"#999999"} />
							</span>
							<Text fontWeight={400} fontSize={16} my={0} color={"#999999"}>
								Om önskade konsulter ej kan bokar vi in en annan konsult åt dig.
							</Text>
						</HStack>
					)}
				</VStack>
				{selectedConsultants.length > 0 && (
					<Flex direction={"column"} gap={3}>
						<Text color={"#999999"} fontWeight={500} fontSize={"14px"}>
							Önskade konsulter &nbsp;
							<Button color={"#219CD0"} variant="link" fontWeight={500} fontSize={"14px"} onClick={onOpen}>
								Ändra
							</Button>
						</Text>
						<Flex direction={"row"} gap={4}>
							{selectedConsultants
								.sort((c) => (c.hasCustomerExperience ? -1 : 1))
								.map((item) => (
									<FinalizedConsultantItem key={item.id} {...item} />
								))}
						</Flex>
					</Flex>
				)}
				<Divider mt={2} />
				<Flex direction={"column"} width={320} mr={"auto"} ml={"auto"} align={"center"} gap={4} pt={4}>
					<Text fontSize={"14px"} fontWeight={400} color={"#999999"}>
						Genom att skicka beställningen bekräftar du att du accepterar{" "}
						<Button
							color={"accent.500"}
							variant="link"
							fontWeight={500}
							fontSize={"14px"}
							textDecor={"underline"}
							onClick={() => setShowTermsModal(true)}
						>
							villkoren
						</Button>{" "}
						(tryck för att öppna)
					</Text>
					<Button
						variant="solid"
						borderRadius={"25px"}
						color="white"
						height="45px"
						bg={"#2A2A2A"}
						_hover={{
							backgroundColor: isSaveButtonDisabled ? "#9fa0a3" : "#2A2A2A",
						}}
						onClick={handleConfirmData}
						isDisabled={isSaveButtonDisabled}
					>
						Skicka beställning
					</Button>
				</Flex>
			</VStack>
			{/* Consultant drawer */}
			<ConsultantSelectMenu
				isLoading={loadingMatches}
				consultantsList={matchingConsultants?.users}
				isOpen={isOpen}
				onClose={onClose}
				onChangeMenuData={handleDrawerData}
				menuData={{
					selectedConsultants: selectedConsultants,
					shouldSystemChooseIfSelectedConsultantsNotAvailable: shouldSystemChooseDefaultConsultant ?? null,
				}}
			/>
			{/* Terms modal menu */}
			<Modal
				onClose={() => {
					setShowTermsModal(false);
				}}
				isOpen={showTermsModal}
				scrollBehavior={"inside"}
				size="4xl"
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Villkoren</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						{loadingCustomerTerms ? <LoadingIndicator size={"s"} /> : <Text whiteSpace="pre-line">{terms}</Text>}
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowTermsModal(false);
							}}
						>
							Stäng
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Order creation loading modal */}
			<Modal
				closeOnOverlayClick={false}
				isOpen={isOpenOrderCreationLoadingModal}
				onClose={onCloseOrderCreationLoadingModal}
			>
				<ModalOverlay />
				<ModalContent p={4}>
					<ModalBody textAlign={"center"}>
						<Box display={"flex"} justifyContent={"center"} my={3}>
							{jobCreationLoading ? <RotatingLoader /> : <BigWhiteCheckIcon />}
						</Box>
						<Text fontWeight={500} fontSize={"20px"} color={"#353535"} m={0}>
							{jobCreationLoading ? "Vänligen vänta, din order skickas ..." : "Uppdraget har skapats"}
						</Text>
						<Text m={0} fontWeight={400} fontSize={"15px"} color={"#999999"}>
							{jobCreationLoading
								? "Stäng inte fönstret"
								: "Uppdraget har skapats och vi jobbar för att tillsätta det så fort som möjligt!"}
						</Text>
					</ModalBody>

					{!jobCreationLoading && (
						<ModalFooter>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="accent.500"
								color={"#FFFFFF"}
								width={"100%"}
								mx={3}
								onClick={() => {
									navigate("/uppdrag");
								}}
							>
								Okej
							</Button>
						</ModalFooter>
					)}
				</ModalContent>
			</Modal>
		</>
	);
};

export default StepThree;
